// ./ui/DareWheel.js
import React, { useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { Howl } from "howler";
import spinSoundFile from "../assets/sounds/spin.mp3";
import winSoundFile from "../assets/sounds/win.mp3";
import jackpotSoundFile from "../assets/sounds/jackpot.mp3";
import losingSoundFile from "../assets/sounds/losing.mp3";
import { Button } from "./button";

const spinSound = new Howl({ src: [spinSoundFile] });
const winSound = new Howl({ src: [winSoundFile] });
const jackpotSound = new Howl({ src: [jackpotSoundFile] });
const losingSound = new Howl({ src: [losingSoundFile] });

const dareSegments = [
  { label: "💎 x2 Prize", effect: "multiply", multiplier: 2 },
  { label: "💥 x3 Prize", effect: "multiply", multiplier: 3 },
  { label: "❌ Lose it all", effect: "lose", payout: 0 },
  { label: "🪄 Mystery Dare", effect: "mystery", payout: -1 },
  { label: "🎲 Rollback", effect: "rollback" },
  { label: "💸 Surprise Loss", effect: "loss", options: [50000, 100000, 250000] },
];

const anglePer = 360 / dareSegments.length;

function getCoordinatesForAngle(angle, radius) {
  const rad = (angle - 90) * (Math.PI / 180);
  return {
    x: radius + radius * Math.cos(rad),
    y: radius + radius * Math.sin(rad),
  };
}

export default function DareWheel({ prizeWon, onComplete }) {
  const controls = useAnimation();
  const [hasSpun, setHasSpun] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);

  const radius = 130;
  const viewBoxSize = radius * 2;

  const spin = () => {
    spinSound.play();

    const weights = [10, 2, 20, 10, 10, 18];
    const total = weights.reduce((a, b) => a + b, 0);
    let rand = Math.random() * total;
    let index = 0;
    for (let i = 0; i < weights.length; i++) {
      if (rand < weights[i]) {
        index = i;
        break;
      }
      rand -= weights[i];
    }

    const pointerAngle = 270;
    const segmentCenterAngle = (index + 0.5) * anglePer;
    const correction = (pointerAngle - segmentCenterAngle + 360 + 90 + 5) % 360;
    const fullSpins = 6;
    const totalRotation = 360 * fullSpins + correction;

    controls.set({ rotate: 0 });
    controls.start({
      rotate: totalRotation,
      transition: {
        duration: 3,
        ease: [0.1, 0.8, 0.3, 1],
      },
    }).then(() => {
      setHasSpun(true);

      const segment = dareSegments[index];
      let outcome = { label: segment.label, payout: 0 };

      if (segment.effect === "multiply") {
        outcome.payout = prizeWon * segment.multiplier;
        winSound.play();
      } else if (segment.effect === "rollback") {
        outcome.payout = prizeWon;
        winSound.play();
      } else if (segment.effect === "mystery") {
        const mysteryOptions = [25000, 50000, 100000, 250000, 500000];
        outcome.payout = mysteryOptions[Math.floor(Math.random() * mysteryOptions.length)];
        winSound.play();
      } else if (segment.effect === "loss") {
        const lossAmount = segment.options[Math.floor(Math.random() * segment.options.length)];
        outcome.payout = Math.max(0, prizeWon - lossAmount);
        losingSound.play();
      } else if (segment.effect === "lose") {
        outcome.payout = 0;
        losingSound.play();
      }

      setResult(outcome);
      setShowResult(true);
      onComplete(outcome);
    });
  };

  return (
    <div className="relative my-6 flex flex-col items-center">
      <h2 className="text-3xl font-extrabold text-red-600 mb-2 flex items-center gap-2">
        <span role="img" aria-label="fire">🔥</span>
        Dare Wheel
        <span role="img" aria-label="fire">🔥</span>
      </h2>

      <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-red-600 z-10 mb-[-4px]" />

      <motion.svg
        width={viewBoxSize}
        height={viewBoxSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        animate={controls}
        initial={{ rotate: 0 }}
        className="rounded-full shadow-[0_0_40px_#dc2626] border-4 border-red-600 bg-black"
      >
        {dareSegments.map((segment, i) => {
          const startAngle = i * anglePer;
          const endAngle = startAngle + anglePer;
          const largeArc = anglePer > 180 ? 1 : 0;

          const start = getCoordinatesForAngle(startAngle, radius);
          const end = getCoordinatesForAngle(endAngle, radius);

          const pathData = `
            M ${radius} ${radius}
            L ${start.x} ${start.y}
            A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}
            Z
          `;

          const fill = i % 2 === 0 ? "#991b1b" : "#1f2937";
          const textColor = "#f87171";

          const midAngle = startAngle + anglePer / 2;
          const textRadius = radius * 0.75;
          const textX = radius + textRadius * Math.cos((midAngle - 90) * (Math.PI / 180));
          const textY = radius + textRadius * Math.sin((midAngle - 90) * (Math.PI / 180));

          return (
            <g key={i}>
              <path d={pathData} fill={fill} />
              <text
                x={textX}
                y={textY}
                fill={textColor}
                fontSize="12"
                fontWeight="bold"
                textAnchor="middle"
                alignmentBaseline="middle"
                transform={`rotate(${midAngle}, ${textX}, ${textY})`}
              >
                {segment.label}
              </text>
            </g>
          );
        })}
      </motion.svg>

      {!hasSpun && (
        <Button
          onClick={spin}
          className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 rounded shadow-lg animate-pulse"
        >
          Spin Dare Wheel 🔥
        </Button>
      )}

      {showResult && result && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-80">
          <div className="rounded-xl p-6 max-w-sm w-full text-center bg-gray-900 border-2 border-red-600 text-white shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-red-400">🔥 Dare Wheel Result</h2>
            <p className="text-xl font-bold mb-4">{result.label}</p>
            <p className="text-md text-white mb-4">
              {result.payout.toLocaleString()} $REGRET
            </p>
            <Button
              onClick={() => setShowResult(false)}
              className="mt-2 bg-cyan-500 text-black font-bold px-4 py-2 rounded"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
